import { Heading } from '@assembly-js/design-system';
import { Grid, IconButton, MenuItem } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormikContext } from 'formik';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { TrashIcon } from 'src/components/icons';
import { VisibilityComparator, VisibilityRuleType } from 'src/constants';
import { useFeatureFlags } from 'src/context/flagsContext';
import { PortalConfigContext } from 'src/context/portalConfigContext';
import {
  RecipientSelector,
  RecipientType,
} from 'src/features/recipient-selector/RecipientSelector';
import { useAppSelector } from 'src/hooks/useStore';
import { MultiSelectOption } from 'src/legacy/components/Select/types';
import { BaseTextField } from 'src/legacy/components/TextField';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { RootState } from 'src/store';
import { CustomFieldEntityType } from 'src/store/clients/types';
import {
  BlackHeadings,
  DarkFont,
  DividersAndCardBorders,
  GraySmall,
  HoverBackground,
  HoverBorder,
  NonHoverBorder,
  white,
} from 'src/theme/colors';
import { DropdownMenuShadow } from 'src/theme/shadows';
import {
  CustomVisibilitySchema,
  expandLegacyValuesToValueItems,
  isCustomFieldRule,
} from 'src/utils/app_visibility';
import { ensureUnreachable } from 'src/utils/common_utils';

import {
  RuleField,
  VisibilityRecipientSelector,
} from './VisibilityRecipientSelector';

type MultiSelectDropdownOption = Omit<MultiSelectOption, 'order' | 'color'> & {
  color: string;
};

export type VisibilityRecipient = {
  clientId: string;
  companyId: string;
  type: RecipientType;
};

type ComparatorField = {
  id: string;
  label: string;
  type: VisibilityRuleType;
  isCompanyCustomField: boolean;
};

type ComparatorRules = {
  id: VisibilityComparator;
  singularLabel: string;
  pluralLabel: string;
};

/**
 * Comparator rules per visibility rule type
 * defines the comparator rules available for each visibility rule type
 * e.g if the visibility rule type is client/company, the available comparator rules are:
 * - IncludesAnyOf (Is) - singularLabel | (Is any of) - pluralLabel
 * if the visibility rule type is custom field, the available comparator rules are:
 * - IncludesAllOf (Includes) - singularLabel | (Includes all of) - pluralLabel
 * - DoesntContainAnyOf (Does not include) - singularLabel | (Doesn’t include any of) - pluralLabel
 */
const COMPARATOR_RULES_PER_VISIBILITY_RULE: Record<
  VisibilityRuleType,
  ComparatorRules[]
> = {
  [VisibilityRuleType.Client]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Is',
      pluralLabel: 'Is any of',
    },
  ],
  [VisibilityRuleType.Company]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Is',
      pluralLabel: 'Is any of',
    },
  ],
  [VisibilityRuleType.CustomField]: [
    {
      id: VisibilityComparator.IncludesAnyOf,
      singularLabel: 'Includes',
      pluralLabel: 'Includes any of',
    },
    {
      id: VisibilityComparator.IncludesAllOf,
      singularLabel: 'Includes',
      pluralLabel: 'Includes all of',
    },
    {
      id: VisibilityComparator.DoesntContainAnyOf,
      singularLabel: 'Does not include',
      pluralLabel: 'Doesn’t include any of',
    },
  ],
  [VisibilityRuleType.NoVisibility]: [],
};

const useStyles = makeStyles()((theme) => ({
  root: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      borderRadius: 0,
    },
  },
  select: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      borderRadius: '4px 0 0 4px',
    },
  },
  autoComplete: {
    '& .MuiTextField-root .MuiOutlinedInput-root': {
      height: '36px',
      borderRadius: '0 4px 4px 0',
      paddingRight: theme.spacing(1.5),
      paddingLeft: theme.spacing(1),
    },
  },
  dropdownIcon: {
    height: '15px',
    width: '15px',
    color: GraySmall,
  },
  list: {
    padding: theme.spacing(0.75, 0),
  },
  listPaper: {
    border: `1px solid ${NonHoverBorder}`,
    boxShadow: DropdownMenuShadow,
    '& .MuiAutocomplete-groupUl': {
      '& .MuiAutocomplete-option': {
        paddingLeft: theme.spacing(2),
      },
    },
  },
  inputRoots: {
    '& .MuiAutocomplete-endAdornment': {
      marginRight: theme.spacing(0.25),
      backgroundColor: white,
    },
  },
  baseChip: {
    borderRadius: 100,
    color: BlackHeadings,
    backgroundColor: HoverBackground,
    border: `1px solid ${NonHoverBorder}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: DividersAndCardBorders,
    },
    '&.MuiChip-root': {
      height: 'auto',
      '& span': {
        padding: 0,
      },
    },
    '&.MuiAutocomplete-tag': {
      margin: 0,
    },
  },
  closeIcon: {
    display: 'none',
  },
  avatarContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1.5),
    gap: theme.spacing(0.75),
  },

  chipLabel: {
    flexShrink: 0,
    maxWidth: '110px',
  },
  deleteIcon: {
    border: `1px solid ${NonHoverBorder}`,
    borderRadius: theme.shape.borderRadius,
    height: theme.spacing(4.5),
    width: theme.spacing(4.5),
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'center',
  },
  trashIcon: {
    fontSize: '20px',
    color: HoverBorder,
  },
  or: {
    color: DarkFont,
    marginBottom: theme.spacing(0.75),
  },

  truncatedText: {
    display: 'inline-block',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  },
  tagIcon: {
    display: 'flex',
  },
  icon: {
    position: 'relative',
    '&:nth-child(2)': {
      left: '-2px',
    },
    '&:nth-child(3)': {
      left: '-4px',
    },
  },
}));

type Props = {
  ruleIndex: number;
  onDeleteRule: () => void;
};

export function getComparatorRules(
  ruleType: VisibilityRuleType,
  selectedValues: string[] = [],
): ComparatorRules[] {
  const rules = COMPARATOR_RULES_PER_VISIBILITY_RULE[ruleType];
  // when tags are selected as visibility rule, we want to avoid
  // rendering duplicate singular comparator labels for
  // IncludesAnyOf and IncludesAllOf. To achieve this, we filter out
  // IncludesAllOf when only one tag field value is selected.
  if (
    ruleType === VisibilityRuleType.CustomField &&
    selectedValues.length <= 1
  ) {
    return rules.filter((r) => r.id !== VisibilityComparator.IncludesAllOf);
  }
  return rules;
}

export const AppVisibilityRule: React.FC<Props> = ({
  ruleIndex,
  onDeleteRule,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { values, setFieldValue, handleChange, errors, touched } =
    useFormikContext<CustomVisibilitySchema>();

  const rule = values?.visibilityConfig?.[ruleIndex];

  const selector = createSelector(
    [
      (state: RootState) => state.clients.companies,
      (state: RootState) => state.clients.activeClients,
    ],
    (allCompanies, clients) => ({
      allCompanies,
      clients,
    }),
  );

  const { allCompanies, clients } = useAppSelector(selector);

  const clientCustomFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFields = useCustomFields(CustomFieldEntityType.COMPANY);
  const portalConfig = useContext(PortalConfigContext);
  const areCompaniesEnabled = !portalConfig.features.disableCompanies;
  const { EnableFeatureAppVisibility: isAppVisibilityFeatureEnabled } =
    useFeatureFlags();

  const clientTagsCustomFields = useMemo(
    () =>
      Object.values(clientCustomFields || {}).filter(
        (field) => field.type === 'multiSelect',
      ),
    [clientCustomFields],
  );
  const companyTagsCustomFields = useMemo(
    () =>
      Object.values(companyCustomFields || {}).filter(
        (field) => field.type === 'multiSelect',
      ),
    [companyCustomFields],
  );

  const clientOptions: MultiSelectDropdownOption[] = useMemo(
    () =>
      clients
        .filter((x) => !!x.fields.givenName && !!x.fields.familyName)
        .map((client) => ({
          id: client.id,
          label: `${client.fields.givenName} ${client.fields.familyName}`,
          color: client.fields.fallbackColor,
        })),
    [clients],
  );

  const companiesOptions: MultiSelectDropdownOption[] = useMemo(
    () =>
      allCompanies
        .filter(
          (company) =>
            !!company.fields.name && company.entityStatus === 'ENABLED',
        )
        .map((company) => ({
          id: company.id,
          label: company.fields.name,
          color: company.fields.fallbackColor ?? '',
        })),
    [allCompanies],
  );

  const clientCustomFieldsDropdown: ComparatorField[] =
    clientTagsCustomFields.map((field) => ({
      id: field.id,
      label: field.name,
      type: VisibilityRuleType.CustomField,
      isCompanyCustomField: false,
    }));

  const companyCustomFieldsDropdown: ComparatorField[] =
    companyTagsCustomFields.map((field) => ({
      id: field.id,
      label: field.name,
      type: VisibilityRuleType.CustomField,
      isCompanyCustomField: true,
    }));

  // Separate arrays for different field types
  const clientCompanyFields: ComparatorField[] = useMemo(
    () => [
      {
        id: 'client',
        label: t(($) => $.client, { count: 1 }),
        type: VisibilityRuleType.Client,
        isCompanyCustomField: false,
      },
      ...(areCompaniesEnabled
        ? [
            {
              id: 'company',
              label: t(($) => $.company, { count: 1 }),
              type: VisibilityRuleType.Company,
              isCompanyCustomField: false,
            },
          ]
        : []),
    ],
    [areCompaniesEnabled, t],
  );

  const clientCustomFieldsForDropdown = useMemo(
    () => clientCustomFieldsDropdown,
    [clientCustomFieldsDropdown],
  );

  const companyCustomFieldsForDropdown = useMemo(
    () => (areCompaniesEnabled ? companyCustomFieldsDropdown : []),
    [areCompaniesEnabled, companyCustomFieldsDropdown],
  );

  // Combined array for field lookup
  const allFields = useMemo(
    () => [
      ...clientCompanyFields,
      ...clientCustomFieldsForDropdown,
      ...companyCustomFieldsForDropdown,
    ],
    [
      clientCompanyFields,
      clientCustomFieldsForDropdown,
      companyCustomFieldsForDropdown,
    ],
  );

  const getComparatorFieldValues = useMemo(
    () =>
      <T extends VisibilityRuleType>(
        comparatorFieldType: T,
        comparatorFieldTypeId: T extends VisibilityRuleType.CustomField
          ? string
          : undefined,
      ) => {
        switch (comparatorFieldType) {
          case VisibilityRuleType.NoVisibility:
            return {
              id: VisibilityRuleType.NoVisibility,
              options: [],
            };
          case VisibilityRuleType.Client:
            return {
              id: VisibilityRuleType.Client,
              options: clientOptions,
            };
          case VisibilityRuleType.Company:
            return {
              id: VisibilityRuleType.Company,
              options: companiesOptions,
            };
          case VisibilityRuleType.CustomField: {
            const customFieldOptions =
              clientTagsCustomFields.find((x) => x.id === comparatorFieldTypeId)
                ?.options ||
              companyTagsCustomFields.find(
                (x) => x.id === comparatorFieldTypeId,
              )?.options ||
              [];
            return {
              id: comparatorFieldTypeId!,
              options: customFieldOptions,
            };
          }
          default:
            ensureUnreachable(comparatorFieldType);
        }
      },
    [
      clientOptions,
      companiesOptions,
      clientTagsCustomFields,
      companyTagsCustomFields,
    ],
  );

  // Get initial custom field values for edit mode
  const getInitialCustomFieldValues = React.useCallback(() => {
    const comparatorFieldId = isCustomFieldRule(rule)
      ? rule.customFieldId
      : undefined;
    const comparatorFieldType = rule.ruleType;
    const comparatorFieldValues = getComparatorFieldValues(
      comparatorFieldType,
      comparatorFieldId,
    );

    return comparatorFieldValues;
  }, [rule, getComparatorFieldValues]);

  const [selectedFieldValues, setSelectedFieldValues] =
    React.useState<RuleField>(() => getInitialCustomFieldValues());

  // Convert valueItems to selectedRecipients for the RecipientSelector (only used when flag is ON)
  // Handles both new valueItems format and legacy values (plain userId strings)
  // Filters out stale entries where the company is no longer assigned to the client
  const clientSelectedRecipients = useMemo<{
    recipients: VisibilityRecipient[];
  }>(() => {
    if (
      !isAppVisibilityFeatureEnabled ||
      rule.ruleType !== VisibilityRuleType.Client
    )
      return {
        recipients: [],
      };

    // Resolve valueItems — use them directly if present, otherwise expand legacy values
    const resolvedItems =
      rule.valueItems && rule.valueItems.length > 0
        ? rule.valueItems
        : expandLegacyValuesToValueItems(rule.values ?? [], clients);

    const recipients: VisibilityRecipient[] = [];
    for (const item of resolvedItems) {
      const client = clients.find((c) => c.id === item.userId);
      if (!client) continue;

      if (item.companyId) {
        // Validate the client still has this company assigned
        if (client.fields.companyIds?.includes(item.companyId)) {
          recipients.push({
            clientId: item.userId,
            companyId: item.companyId,
            type: 'client',
          });
        }
      } else {
        // No company — show with empty companyId
        recipients.push({
          clientId: item.userId,
          companyId: '',
          type: 'client',
        });
      }
    }
    return { recipients };
  }, [rule, clients, isAppVisibilityFeatureEnabled]);

  // Handle RecipientSelector changes — save both valueItems and values
  const handleClientRecipientChange: React.ComponentProps<
    typeof RecipientSelector
  >['onRecipientSelected'] = React.useCallback(
    (inputValues) => {
      const newValueItems = inputValues.map((v) => ({
        userId: v.id,
        ...(v.companyId ? { companyId: v.companyId } : {}),
      }));
      setFieldValue(`visibilityConfig.${ruleIndex}.valueItems`, newValueItems);
      // Keep values populated with unique userIds for validation and backward compat
      const uniqueUserIds = [...new Set(inputValues.map((v) => v.id))];
      setFieldValue(`visibilityConfig.${ruleIndex}.values`, uniqueUserIds);
    },
    [setFieldValue, ruleIndex],
  );

  // This useEffect makes sure that the selected custom fields values
  // dropdown is updated when the custom fields are fetched. Without
  // doing this, the dropdown will initially be empty when the custom
  // fields are fetched.
  React.useEffect(() => {
    if (!clientCustomFields && !companyCustomFields) return;
    setSelectedFieldValues(getInitialCustomFieldValues());
  }, [clientCustomFields, companyCustomFields, getInitialCustomFieldValues]);

  const handleFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const comparatorFieldId = event.target.value;
    const selectedComparatorField = allFields.find(
      (field) => field.id === comparatorFieldId,
    );
    if (selectedComparatorField) {
      setFieldValue(
        `visibilityConfig.${ruleIndex}.ruleType`,
        selectedComparatorField.type,
      );
      setFieldValue(
        `visibilityConfig.${ruleIndex}.isCompanyCustomField`,
        selectedComparatorField.isCompanyCustomField,
      );
      const comparatorFieldValues = getComparatorFieldValues(
        selectedComparatorField.type,
        comparatorFieldId,
      );
      setSelectedFieldValues(comparatorFieldValues);
      setFieldValue(`visibilityConfig.${ruleIndex}.values`, []);
      // For custom fields, we need to send `customFieldId` to the backend
      if (selectedComparatorField.type === VisibilityRuleType.CustomField) {
        setFieldValue(
          `visibilityConfig.${ruleIndex}.customFieldId`,
          comparatorFieldId,
        );
      }
    }
  };

  const errorContainer = errors.visibilityConfig?.[ruleIndex];

  const optionDropdownHelperText =
    !!touched.visibilityConfig?.[ruleIndex]?.values &&
    typeof errorContainer !== 'string' &&
    errorContainer?.values;

  const visibilityConfigValue = values.visibilityConfig?.[ruleIndex];
  const {
    comparator: selectedComparatorValue,
    ruleType: selectedRuleType,
    values: selectedValues = [],
  } = visibilityConfigValue;

  const comparatorDropdownRules = useMemo(
    () => getComparatorRules(selectedRuleType, selectedValues),
    [selectedRuleType, selectedValues],
  );

  return (
    <Grid container className={classes.root} spacing={3}>
      {/* Leave space for delete icon when there are more than one rules */}
      <Grid xs={values.visibilityConfig?.length > 1 ? 11 : 12} item container>
        <Grid item xs={4} className={classes.select}>
          <BaseTextField
            fullWidth
            select
            sizeVariant="medium"
            variant="outlined"
            value={selectedFieldValues.id}
            onChange={handleFieldChange}
          >
            {/* Client and Company fields */}
            {clientCompanyFields.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}

            {/* Client custom fields heading */}
            {clientCustomFieldsForDropdown.length > 0 && (
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.page_apps.client_custom_fields)}
                </Heading>
              </MenuItem>
            )}

            {/* Client custom fields */}
            {clientCustomFieldsForDropdown.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}

            {/* Company custom fields heading */}
            {companyCustomFieldsForDropdown.length > 0 && (
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.page_apps.company_custom_fields)}
                </Heading>
              </MenuItem>
            )}

            {/* Company custom fields */}
            {companyCustomFieldsForDropdown.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.label}
              </MenuItem>
            ))}
          </BaseTextField>
        </Grid>
        <Grid item xs={4}>
          <BaseTextField
            fullWidth
            select
            sizeVariant="medium"
            variant="outlined"
            name={`visibilityConfig.${ruleIndex}.comparator`}
            value={
              comparatorDropdownRules.find(
                (r) => r.id === selectedComparatorValue,
              )?.id || VisibilityComparator.IncludesAnyOf
            }
            onChange={handleChange}
          >
            {comparatorDropdownRules.map((comparatorRule) => (
              <MenuItem key={comparatorRule.id} value={comparatorRule.id}>
                {selectedValues?.length > 1
                  ? comparatorRule.pluralLabel
                  : comparatorRule.singularLabel}
              </MenuItem>
            ))}
          </BaseTextField>
        </Grid>
        <Grid
          item
          xs={4}
          className={
            selectedRuleType === VisibilityRuleType.Client &&
            isAppVisibilityFeatureEnabled
              ? undefined
              : classes.autoComplete
          }
        >
          <VisibilityRecipientSelector
            ruleIndex={ruleIndex}
            recipients={clientSelectedRecipients.recipients}
            clients={clients}
            selectedFieldValues={selectedFieldValues}
            selectedRuleType={selectedRuleType}
            onRecipientSelected={handleClientRecipientChange}
            error={
              !!touched.visibilityConfig?.[ruleIndex]?.values &&
              !!optionDropdownHelperText
            }
            helperText={
              typeof optionDropdownHelperText === 'string'
                ? optionDropdownHelperText
                : undefined
            }
          />
        </Grid>
      </Grid>
      {values.visibilityConfig?.length > 1 && (
        <Grid item xs={1}>
          <div className={classes.deleteIcon}>
            <IconButton onClick={onDeleteRule}>
              <TrashIcon className={classes.trashIcon} />
            </IconButton>
          </div>
        </Grid>
      )}
    </Grid>
  );
};
